import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import redflag from "../../../../assets/images/bahrainsmlogo.svg";
import useHomeFlow from "../../../../hooks/useHomeFlowSimple";
import useHomeFormData from "../../../../hooks/useHomeFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import BottomStickyBtnLayout from "../../../../pages/motorInsurancePages/newMotorPolicy/Layouts/BottomStickyBtnLayout";
import { selectHomeFormData } from "../../../../redux/features/homeFormSlice";
import Input from "../../../common/Input/Input";
import HomeSummaryModal from "../../../newHomePolicy/modals/homeSummaryModal";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import NewHomeJourneyLayout from "../../layouts/NewHomeJourneyLayout";

const HomeDetails = () => {
  const translate = useLanguageTranslate();
  const { handleNextSubstep, handlePrevSubstep } = useHomeFlow();
  const { formData, saveHomeDataInRedux } = useHomeFormData("homeDetails");
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get all home form data for premium calculation
  const allHomeFormData = useSelector(selectHomeFormData);

  // Form state
  const [buildingValue, setBuildingValue] = useState("");
  const [accidentalDamage, setAccidentalDamage] = useState(false);
  const [landslideSubsidence, setLandslideSubsidence] = useState(false);
  const [contentsDetails, setContentsDetails] = useState(false);
  const [contentsValue, setContentsValue] = useState("");
  const [personalBelongingsValue, setPersonalBelongingsValue] = useState("");
  const [contentsAccidentalDamage, setContentsAccidentalDamage] =
    useState(false);
  const [contentsLandslideSubsidence, setContentsLandslideSubsidence] =
    useState(false);
  const [errors, setErrors] = useState({});
  const [showHomeSummaryModal, setShowHomeSummaryModal] = useState(false);

  // Initialize form with existing data - run only once on mount
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      setBuildingValue(formData.buildingValue || "");
      setAccidentalDamage(formData.accidentalDamage || false);
      setLandslideSubsidence(formData.landslideSubsidence || false);
      setContentsDetails(formData.contentsDetails || false);
      setContentsValue(formData.contentsValue || "");
      setPersonalBelongingsValue(formData.personalBelongingsValue || "");
      setContentsAccidentalDamage(formData.contentsAccidentalDamage || false);
      setContentsLandslideSubsidence(
        formData.contentsLandslideSubsidence || false
      );
    }
  }, []);

  // Helper function to clean and parse numeric values
  const parseCleanFloat = (value) => {
    return parseFloat(String(value).replace(/[,\s]/g, ""));
  };

  // Validate individual field on blur
  const validateField = (fieldName, value) => {
    let error = "";

    if (!value || value.trim() === "") {
      error = translate("This field is required.", "هذا المبلغ مطلوب.");
    } else if (parseCleanFloat(value) <= 0) {
      error = translate(
        "This value must be greater than 0 BHD.",
        "يجب أن يكون هذا المبلغ أكبر من 0 د.ب."
      );
    } else if (parseCleanFloat(value) > 500000) {
      error = translate(
        "The value must not exceed BHD 500,000.",
        "يجب أن لا تتجاوز القيمة 500,000 د.ب."
      );
    }

    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));

    return error === "";
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate Building Value (always required)
    if (!buildingValue || buildingValue.trim() === "") {
      newErrors.buildingValue = translate(
        "This field is required.",
        "هذا المبلغ مطلوب."
      );
    } else if (parseCleanFloat(buildingValue) <= 0) {
      newErrors.buildingValue = translate(
        "This value must be greater than 0 BHD.",
        "يجب أن يكون هذا المبلغ أكبر من 0 د.ب."
      );
    } else if (parseCleanFloat(buildingValue) > 500000) {
      newErrors.buildingValue = translate(
        "The value must not exceed BHD 500,000.",
        "يجب أن لا تتجاوز القيمة 500,000 د.ب."
      );
    }

    // Validate Contents details fields (always required now since section is always visible)
    if (true) { // Changed from contentsDetails to true since section is always visible
      if (!contentsValue || contentsValue.trim() === "") {
        newErrors.contentsValue = translate(
          "This field is required.",
          "هذا المبلغ مطلوب."
        );
      } else if (parseCleanFloat(contentsValue) <= 0) {
        newErrors.contentsValue = translate(
          "This value must be greater than 0 BHD.",
          "يجب أن يكون هذا المبلغ أكبر من 0 د.ب."
        );
      } else if (parseCleanFloat(contentsValue) > 500000) {
        newErrors.contentsValue = translate(
          "The value must not exceed BHD 500,000.",
          "يجب أن لا تتجاوز القيمة 500,000 د.ب."
        );
      }

      if (!personalBelongingsValue || personalBelongingsValue.trim() === "") {
        newErrors.personalBelongingsValue = translate(
          "This field is required.",
          "هذا المبلغ مطلوب."
        );
      } else if (parseCleanFloat(personalBelongingsValue) <= 0) {
        newErrors.personalBelongingsValue = translate(
          "This value must be greater than 0 BHD.",
          "يجب أن يكون هذا المبلغ أكبر من 0 د.ب."
        );
      } else if (parseCleanFloat(personalBelongingsValue) > 500000) {
        newErrors.personalBelongingsValue = translate(
          "The value must not exceed BHD 500,000.",
          "يجب أن لا تتجاوز القيمة 500,000 د.ب."
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to validate total SumInsured
  const validateSumInsured = () => {
    // Helper function to clean and parse numeric values
    const parseCleanFloat = (value) => {
      if (!value || value === "") return 0;
      return parseFloat(String(value).replace(/[,\s]/g, "")) || 0;
    };

    // Calculate total SumInsured = Building Value + Content Value + Personal Belongings Value
    const buildingVal = parseCleanFloat(buildingValue);
    // Always include content values since Content Details section is always visible now
    const contentVal = parseCleanFloat(contentsValue) || 0;
    const personalBelongingsVal = parseCleanFloat(personalBelongingsValue) || 0;

    const totalSumInsured = buildingVal + contentVal + personalBelongingsVal;

    // Check if total exceeds 500,000 BHD (exactly 500,000 is allowed)
    if (totalSumInsured > 500000) {
      toast.error(
        translate(
          "Total Sum Insured cannot exceed BHD 500,000.",
          "يجب أن لا تتجاوز قيمة مبلغ التأمين 500,000 د.ب."
        )
      );
      return false;
    }

    return true;
  };

  const handleContinue = () => {
    // Save current form data to Redux store
    const currentFormData = {
      buildingValue,
      accidentalDamage,
      landslideSubsidence,
      contentsDetails,
      contentsValue,
      personalBelongingsValue,
      contentsAccidentalDamage,
      contentsLandslideSubsidence,
    };

    saveHomeDataInRedux(currentFormData);

    // Validate individual fields first
    if (!validateForm()) {
      return;
    }

    // Then validate total SumInsured
    if (!validateSumInsured()) {
      return;
    }

    // If all validations pass, proceed to next step
    handleNextSubstep();
  };

  return (
    <div className="w-full">
      <NewHomeJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_label={translate("Continue", "التالي")}
          show_summary={false}
          passedFrom="HomeInsurance"
        >
          <div className="flex justify-center items-start min-h-[60vh]">
            <div className="w-full max-w-[600px] mx-auto px-4">
              <TitleDescription
                title={translate("Home", "معلومات عن منزلك")}
                description={translate(
                  "Tell us a bit about your place.",
                  "أخبرنا المزيد عن منزلك."
                )}
              />

              <div className="space-y-6 mt-8">
                {/* Building details section */}
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-[#252C32] pb-2 border-b border-gray-200">
                    {translate("Building details", "معطيات المبنى")}
                  </h3>

                  {/* Building Value Input */}
                  <div className="space-y-2">
                    <label className="block text-base font-normal rtl:font-medium text-[#48535B]">
                      {translate(
                        "Building Value including Fixtures and Fittings",
                        "قيمة المبنى بما في ذلك التركيبات والتجهيزات"
                      )}
                    </label>
                    <p className="text-xs font-normal rtl:font-medium text-[#48535B]">
                      {translate(
                        "A Concrete Structure with a roof, walls and permanent fixtures",
                        "هيكل خرساني مزود بسقف وجدران وتجهيزات دائمة"
                      )}
                    </p>
                    <Input
                      className="px-4 py-6 max767:items-center max767:w-full text-2xl not-italic font-normal"
                      endAdornment={
                        <div className="flex align-middle mt-1">
                          <img src={redflag} alt="flag" />
                          <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                            {translate("BHD", "د.ب")}
                          </p>
                        </div>
                      }
                      type="text"
                      formatNumber={true}
                      name="buildingValue"
                      value={buildingValue}
                      handleChange={(e) => setBuildingValue(e.target.value)}
                      onBlur={() =>
                        validateField("buildingValue", buildingValue)
                      }
                      error={errors.buildingValue}
                    />
                  </div>

                  {/* COMMENTED OUT - Building Accidental Damage Switch */}
                  {/* <div
                    className={`bg-white rounded-lg border-2 p-4 ${
                      accidentalDamage ? "border-[#00719A]" : "border-[#DDE2E4]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg text-[#48535B]">
                        {translate("Accidental Damage", "الأضرار العرضية")}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={accidentalDamage}
                          onChange={(e) =>
                            setAccidentalDamage(e.target.checked)
                          }
                        />
                        <div
                          className={`w-8 h-5 rounded-full transition-colors ${
                            accidentalDamage ? "bg-[#00A9E7]" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                              accidentalDamage
                                ? isRTL
                                  ? "-translate-x-3.5"
                                  : "translate-x-3.5"
                                : isRTL
                                ? "-translate-x-[0.15rem]"
                                : "translate-x-[0.15rem]"
                            }`}
                          ></div>
                        </div>
                      </label>
                    </div>
                    <div className="flex justify-end">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                          accidentalDamage
                            ? "bg-[#00719A] text-white"
                            : "bg-[#EEF0F2] text-[#252C32]"
                        }`}
                      >
                        {isRTL ? "+1 د.ب" : "+ BHD 1"}
                      </span>
                    </div>
                  </div> */}

                  {/* COMMENTED OUT - Building Landslide / Subsidence Switch */}
                  {/* <div
                    className={`bg-white rounded-lg border-2 p-4 ${
                      landslideSubsidence
                        ? "border-[#00719A]"
                        : "border-[#DDE2E4]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg text-[#48535B]">
                        {translate(
                          "Landslide / Subsidence",
                          "انزلاق أرضي/انحدار أرضي"
                        )}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={landslideSubsidence}
                          onChange={(e) =>
                            setLandslideSubsidence(e.target.checked)
                          }
                        />
                        <div
                          className={`w-8 h-5 rounded-full transition-colors ${
                            landslideSubsidence ? "bg-[#00A9E7]" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                              landslideSubsidence
                                ? isRTL
                                  ? "-translate-x-3.5"
                                  : "translate-x-3.5"
                                : isRTL
                                ? "-translate-x-[0.15rem]"
                                : "translate-x-[0.15rem]"
                            }`}
                          ></div>
                        </div>
                      </label>
                    </div>
                    <div className="flex justify-end">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                          landslideSubsidence
                            ? "bg-[#00719A] text-white"
                            : "bg-[#EEF0F2] text-[#252C32]"
                        }`}
                      >
                        {isRTL ? "+1 د.ب" : "+ BHD 1"}
                      </span>
                    </div>
                  </div> */}
                </div>

                {/* Contents details section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-[#252C32]">
                      {translate("Contents details", "محتويات المنزل")}
                    </h3>
                    {/* COMMENTED OUT - Content Details Toggle Switch */}
                    {/* <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={contentsDetails}
                        onChange={(e) => setContentsDetails(e.target.checked)}
                      />
                      <div
                        className={`w-8 h-5 rounded-full transition-colors ${
                          contentsDetails ? "bg-[#00A9E7]" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                            contentsDetails
                              ? isRTL
                                ? "-translate-x-3.5"
                                : "translate-x-3.5"
                              : isRTL
                              ? "-translate-x-[0.15rem]"
                              : "translate-x-[0.15rem]"
                          }`}
                        ></div>
                      </div>
                    </label> */}
                  </div>

                  {/* Content details section - always visible now (removed toggle condition) */}
                  {true && (
                    <div className="space-y-4 mb-8">
                      {/* Content and Furniture value */}
                      <div className="space-y-2">
                        <label className="block text-base font-normal rtl:font-medium text-[#48535B]">
                          {translate(
                            "Content and Furniture value",
                            "قيمة المحتوى والأثاث"
                          )}
                        </label>
                        <p className="text-xs font-normal rtl:font-medium text-[#48535B]">
                          {translate(
                            "The movable furniture like sofas, tables, electronic appliances etc.",
                            "الأثاث المتحرك مثل الأرائك والطاولات والأجهزة الإلكترونية وغيرها."
                          )}
                        </p>
                        <Input
                          className="px-4 py-6 max767:items-center max767:w-full text-2xl not-italic font-normal"
                          endAdornment={
                            <div className="flex align-middle mt-1">
                              <img src={redflag} alt="flag" />
                              <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                                {translate("BHD", "د.ب")}
                              </p>
                            </div>
                          }
                          type="text"
                          formatNumber={true}
                          name="contentsValue"
                          value={contentsValue}
                          handleChange={(e) => setContentsValue(e.target.value)}
                          onBlur={() =>
                            validateField("contentsValue", contentsValue)
                          }
                          error={errors.contentsValue}
                        />
                      </div>

                      {/* Personal Belongings value */}
                      <div className="space-y-2">
                        <label className="block text-base font-normal rtl:font-medium text-[#48535B]">
                          {translate(
                            "Personal Belongings value",
                            "قيمة الممتلكات الشخصية"
                          )}
                        </label>
                        <p className="text-xs font-normal rtl:font-medium text-[#48535B]">
                          {translate(
                            "Personal items like sports gear and clothing.",
                            "الأغراض الشخصية مثل المعدات الرياضية والملابس."
                          )}
                        </p>
                        <Input
                          className="px-4 py-6 max767:items-center max767:w-full text-2xl not-italic font-normal"
                          endAdornment={
                            <div className="flex align-middle mt-1">
                              <img src={redflag} alt="flag" />
                              <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                                {translate("BHD", "د.ب")}
                              </p>
                            </div>
                          }
                          type="text"
                          formatNumber={true}
                          name="personalBelongingsValue"
                          value={personalBelongingsValue}
                          handleChange={(e) =>
                            setPersonalBelongingsValue(e.target.value)
                          }
                          onBlur={() =>
                            validateField(
                              "personalBelongingsValue",
                              personalBelongingsValue
                            )
                          }
                          error={errors.personalBelongingsValue}
                        />
                      </div>

                      {/* COMMENTED OUT - Contents Accidental Damage Switch */}
                      {/* <div
                        className={`bg-white rounded-lg border-2 p-4 ${
                          contentsAccidentalDamage
                            ? "border-[#00719A]"
                            : "border-[#DDE2E4]"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg text-[#48535B]">
                            {translate("Accidental Damage", "الأضرار العرضية")}
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={contentsAccidentalDamage}
                              onChange={(e) =>
                                setContentsAccidentalDamage(e.target.checked)
                              }
                            />
                            <div
                              className={`w-8 h-5 rounded-full transition-colors ${
                                contentsAccidentalDamage
                                  ? "bg-[#00A9E7]"
                                  : "bg-gray-300"
                              }`}
                            >
                              <div
                                className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                                  contentsAccidentalDamage
                                    ? isRTL
                                      ? "-translate-x-3.5"
                                      : "translate-x-3.5"
                                    : isRTL
                                    ? "-translate-x-[0.15rem]"
                                    : "translate-x-[0.15rem]"
                                }`}
                              ></div>
                            </div>
                          </label>
                        </div>
                        <div className="flex justify-end">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                              contentsAccidentalDamage
                                ? "bg-[#00719A] text-white"
                                : "bg-[#EEF0F2] text-[#252C32]"
                            }`}
                          >
                            {translate("+ BHD 1", "+ د.ب 1")}
                          </span>
                        </div>
                      </div> */}

                      {/* COMMENTED OUT - Contents Landslide / Subsidence Switch */}
                      {/* <div
                        className={`bg-white rounded-lg border-2 p-4 ${
                          contentsLandslideSubsidence
                            ? "border-[#00719A]"
                            : "border-[#DDE2E4]"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg text-[#48535B]">
                            {translate(
                              "Landslide / Subsidence",
                              "انزلاق أرضي/انحدار أرضي"
                            )}
                          </span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={contentsLandslideSubsidence}
                              onChange={(e) =>
                                setContentsLandslideSubsidence(e.target.checked)
                              }
                            />
                            <div
                              className={`w-8 h-5 rounded-full transition-colors ${
                                contentsLandslideSubsidence
                                  ? "bg-[#00A9E7]"
                                  : "bg-gray-300"
                              }`}
                            >
                              <div
                                className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                                  contentsLandslideSubsidence
                                    ? isRTL
                                      ? "-translate-x-3.5"
                                      : "translate-x-3.5"
                                    : isRTL
                                    ? "-translate-x-[0.15rem]"
                                    : "translate-x-[0.15rem]"
                                }`}
                              ></div>
                            </div>
                          </label>
                        </div>
                        <div className="flex justify-end">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
                              contentsLandslideSubsidence
                                ? "bg-[#00719A] text-white"
                                : "bg-[#EEF0F2] text-[#252C32]"
                            }`}
                          >
                            {translate("+ BHD 1", "+ د.ب 1")}
                          </span>
                        </div>
                      </div> */}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </BottomStickyBtnLayout>
      </NewHomeJourneyLayout>

      {/* Home Summary Modal */}
      <HomeSummaryModal
        show={showHomeSummaryModal}
        onClose={() => setShowHomeSummaryModal(false)}
        homeFormData={{
          ...allHomeFormData,
          homeDetails: {
            buildingValue,
            accidentalDamage,
            landslideSubsidence,
            contentsDetails,
            contentsValue,
            personalBelongingsValue,
            contentsAccidentalDamage,
            contentsLandslideSubsidence,
          },
        }}
      />
    </div>
  );
};

export default HomeDetails;
